/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { formatCurrency, getCurrencySymbol, NgClass, AsyncPipe, CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Optional,
  ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCalendarCellClassFunction, MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { AddToCartComponent } from '@spartacus/cart/base/components/add-to-cart';
import { CartItemComponentOptions } from '@spartacus/cart/base/root';
import { BaseSiteService, CmsAddToCartComponent, EventService, GlobalMessageService, GlobalMessageType, Product, Stock, VariantOption, isNotNullable, TranslatePipe } from '@spartacus/core';
import { CmsComponentData, CurrentProductService, ProductListItemContext, SpinnerComponent } from '@spartacus/storefront';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { UdxCartAddEntrySuccessEvent } from '../../event/udx-cart.events';
import { UdxActiveCartFacade } from '../../facade/udx-active-cart.facade';
import { Udxb2bCalendarFacade } from '../../facade/udxb2b-calendar-facade';
import { UdxDataTransferService } from '../../services/udx-data-transfer.service.service';
import { USJB2BPortalConfig, storeFrontConfig } from '../../shared/constant/storefront-config';
import { UDXAppConstants } from '../../utilities/constants/udx-app.constants';
import { UDXCartUiEventAddToCart } from '../added-to-cart-dialog/udx-added-to-cart-dialog-event.listener';
import { Udxb2bDateAndTimeAvailabityResponse, Udxb2bInventoryEvents } from '../model';
import { UdxB2bProductSummaryComponent } from '../product/product-summary/product-summary.component';
import { Udxb2bItemCounterComponent } from '../item-counter/item-counter.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatOptionModule } from '@angular/material/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';


@Component({
    selector: 'cx-add-to-cart',
    templateUrl: './add-to-cart.component.html',
    styleUrls: ['./add-to-cart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [SpinnerComponent, ReactiveFormsModule, UdxB2bProductSummaryComponent, NgClass, Udxb2bItemCounterComponent, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatProgressSpinnerModule, MatSelectModule, MatOptionModule, MatGridListModule, MatCardModule, AsyncPipe, CurrencyPipe, TranslatePipe]
})
export class Udxb2bAddToCartComponent extends AddToCartComponent {
  apiLoading$ = new BehaviorSubject(false);
  readonly appConstants = UDXAppConstants;
  @Input() override options: CartItemComponentOptions = {};

  COLONSEPERATOR = ':';

  isDateSelectionDisabled = true;

  _isAddToCartDisabled = true;
  currentBaseSite!: string;
  dateLabel:string = 'DD/MM/YYYY';
  defaultCurrencySymbolForBaseStore: any;
  defaultCurrencyISO: any;
  subtotalFormattedString: any;
   fieldEnabled = false;
  public get isAddToCartDisabled() {
    return this._isAddToCartDisabled;
  }
  public set isAddToCartDisabled(newValue: boolean) {
    this._isAddToCartDisabled = newValue;
  }

  subtotal = new BehaviorSubject(0);

  availableTimeSlotAndPrice = new Map();

  timeSlots: Set<string> = new Set<string>();

  isLoadingDates = false;

  currentSingleSelectedOption: any;
  selectedVariantName: any;

  activeSingleSelectButton: any = 0;

  numberOfVariants: number = 0;
  isDateSelected: boolean = false;
  unitPrice=0;

  cartAddEntrySuccessResult$?: Observable<any>;
  filteredEntries: any[] = [];
  taxEnabled !:string;
  usjfeature = false;
  constructor(
    protected udxb2bCalendarFacade: Udxb2bCalendarFacade,
    currentProductService: CurrentProductService,
    cd: ChangeDetectorRef,
    override activeCartService: UdxActiveCartFacade,
    component: CmsComponentData<CmsAddToCartComponent>,
    eventService: EventService,
    public dialog: MatDialog,
    protected dataTransfer: UdxDataTransferService,
    protected globalMessageService: GlobalMessageService,
    private _baseSiteService: BaseSiteService,

    @Optional() productListItemContext?: ProductListItemContext,
  ) {
    super(currentProductService, cd, activeCartService, component, eventService, productListItemContext);
    this.subscription.add(
    this.addToCartSubject.pipe().subscribe((shouldDisableDate) => {
      this.performAddToCart(shouldDisableDate);
    })
  );
    this.getCurrentStoreBaseSite();
  }

  dateFilter = (date: any): boolean => {
    return false;
  };
  blockOutDateCell: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    // Only highligh dates inside the month view.
    if (view === 'month' && this.product.annualPassAvailableDates != undefined) {
      const dateSearch = this.dateToString(new Date(cellDate)).trim();
      return this.product.annualPassAvailableDates.includes(dateSearch) ? 'blockout-date-class' : '';
    }
    return '';
  };

  override ngOnInit() {
    // this.getCurrentStoreBaseSite();
    if (this.product) {
      this.productCode = this.product.code ?? '';
      this.setStockInfo(this.product);
      this.cd.markForCheck();
    } else if (this.productCode) {
      // force hasStock and quantity for the time being, as we do not have more info:
      this.quantity = 1;
      this.hasStock = true;
      this.cd.markForCheck();
    } else {
      this.subscription = (
        this.productListItemContext ? this.productListItemContext.product$ : this.currentProductService.getProduct()
      )
        .pipe(filter(isNotNullable))
        .subscribe((product) => {
          this.productCode = product.code ?? '';
          this.product = product;
          this.buildDynamicForms();
          this.setStockInfo(product);
          //check for tax field
          this.product.hasTaxes ? (this.taxEnabled = 'pdp.plusTax') : (this.taxEnabled = '');
          this.cd.markForCheck();
        });
    }
    this.instantiateFormattedTotalObservable();
  }

  protected instantiateFormattedTotalObservable() {
    this.subscription.add(
    this.subtotal.subscribe({
      next: (total) => {
        // alert ("The total changed to: " + total);
        let currencySymbol = getCurrencySymbol(
          this.product?.price?.currencyIso ??  '',
          'narrow'
        );
        let formattedTotal = formatCurrency(
          total,
          'en',
          currencySymbol,
          this.product?.price?.currencyIso ?? 'JPY'
        );
        // this.subtotalFormattedString.next(formattedTotal);
      }
    })
  );
  }

  getDefaultPerfomance(): string {
    const [first] = this.timeSlots;
    return first;
  }

  buildDynamicForms() {
    const formGroupFields = this.getFormControlsFields();
    this.addToCartForm = new UntypedFormGroup(formGroupFields);
  }

  getFormControlsFields() {
    const formGroupFields = {} as string | any;
    if (
      (this.product.productVariantSelector == 'SINGLE_SELECT' || this.product.udxProductType === 'EXPRESS') &&
      this.product.variantOptions
    ) {
      this.numberOfVariants = +this.product?.variantOptions!.length;
      formGroupFields['quantity'] = new UntypedFormControl({
        value: 1,
        disabled: true,
      });
      this.product.variantOptions?.forEach((option) => {
        if (option.code != null) {
          formGroupFields[option.code] = new UntypedFormControl({
            value: 0,
            disabled: this.stepperBtnDisabled(option.stock),
          });
        }
      });
    } else {
      this.product.variantOptions?.forEach((option) => {
        if (option.code != null) {
          formGroupFields[option.code] = new UntypedFormControl({
            value: 0,
            disabled: this.stepperBtnDisabled(option.stock),
          });
        }
      });
    }
    if (this.product.variantOptions === undefined) {
      const quantityBtn = this.stepperBtnDisabled(this.product.stock);
      formGroupFields['quantity'] = new UntypedFormControl({ value: 0, disabled: quantityBtn });
      if (this.product.bundlePrice != undefined && !quantityBtn && !this.product.dateSelectionRequired) {
        this.subtotal.next(this.product.bundlePrice.totalPrice!);
      } else if (this.product.price && !quantityBtn) {
        this.subtotal.next(this.product.price.value!);
      }
      this.isAddToCartDisabled = !(this.subtotal.getValue() > 0);
    }
    if (this.product.dateSelectionRequired) {
      formGroupFields['datePicker'] = new UntypedFormControl({ value: [''], disabled: true }); // input feild is always disabled
      if (this.product.bundlePrice != undefined) {
        this.isDateSelectionDisabled = false;
      }
    }
    if (this.product.timeSelectionRequired) {
      formGroupFields['timeSlotPicker'] = new UntypedFormControl({ value: [''], disabled: true });
    }
    return formGroupFields;
  }

  stepperBtnDisabled(stockData: Stock | undefined): boolean {
    return (
      stockData === undefined || stockData.stockLevelStatus === undefined || stockData.stockLevelStatus === 'outOfStock'
    );
  }

  onPickerToggle() {
    if (!this.isDateSelectionDisabled) {
      this.isLoadingDates = true;
         this.subscription.add(
      this.udxb2bCalendarFacade.fetchAvailableDatesForPdP(this.addToCartForm, this.product).subscribe(
        (response) => {
          this.availableTimeSlotAndPrice = new Map();
          this.caluclateAvailableDateAndTimeSlot(response);
        },
        (error) => {
          console.error('Error loading available dates', error);
          this.isLoadingDates = false;
        }
      )
    );
    }
  }

  private caluclateAvailableDateAndTimeSlot(response: Udxb2bDateAndTimeAvailabityResponse) {
    const dates = response.eventAvailability?.flatMap((calAvail) => calAvail.calendarDates?.map((date) => date.date));
    const availableDates = dates?.filter((date) => {
      return response.eventAvailability?.every((avail) => {
        return avail.calendarDates?.some(
          (calDate) =>
            calDate.date === date &&
            calDate.pricing &&
            calDate.pricing?.length > 0
        );
      });
    });
    response.eventAvailability?.forEach((event) => {
      this.availableTimeSlotAndPrice.set(event.partNumber, event.calendarDates);
    });
    this.dateFilter = (date: any): boolean => {
      const dateSearch = this.dateToString(new Date(date)).trim();
      const uniqueElements = [...new Set(availableDates)];
      return uniqueElements.includes(dateSearch);
    };
    this.isLoadingDates = false;
    this.cd.detectChanges();
  }

  dateToString(date: Date): string {
    return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
  }

  /**
   * Below code to be changed while addtocart
   * @param product
   */
  protected override setStockInfo(product: Product): void {
    this.quantity = 1;
    this.hasStock = Boolean(product.stock?.stockLevelStatus !== 'outOfStock');

    this.inventoryThreshold = product.stock?.isValueRounded ?? false;

    if (this.hasStock && product.stock?.stockLevel) {
      this.maxQuantity = product.stock.stockLevel;
    }

    if (this.productListItemContext) {
      this.showQuantity = false;
    }
  }

  onQuantityChanged(quantity: number, hideOnCart: boolean | undefined): void {
    if (hideOnCart != undefined && hideOnCart) {
      this.globalMessageService.add({ key: 'pdp.hideOnCart_warning' }, GlobalMessageType.MSG_TYPE_WARNING);
      return;
    }

    if (this.product.productVariantSelector == 'SINGLE_SELECT' && this.product.variantOptions) {
      this.addToCartForm
        .get(this.currentSingleSelectedOption.code)
        ?.setValue(this.addToCartForm.get('quantity')?.value);
    }
    if (quantity > 0 || this.hasPositiveQuantity(this.product)) {
      this.enableOrDisableBtn();
    } else {
      if (this.product.dateSelectionRequired) {
        this.isDateSelectionDisabled = true;
        this.addToCartForm.get('datePicker')!.reset();
        this.timeSelectionBtn();
      }
      this.isAddToCartDisabled = true;
      this.subtotal.next(0);
      // Raj: Remove the date placeholder text black bold here
      document.querySelector("input[formControlName='datePicker']")?.closest('mat-form-field')?.classList.remove('placeholderBold');
    }
    this.updateSubtotalForPackageOrNormalPrd();
  }

  updateSubtotalForPackageOrNormalPrd() {
    if (this.product.bundlePrice != undefined && !this.product.dateSelectionRequired) {
      const packagePrice = this.addToCartForm.get('quantity')?.value * this.product.bundlePrice.totalPrice!;
      this.subtotal.next(packagePrice);
      this.isAddToCartDisabled = !(this.subtotal.getValue() > 0);
    } else if (this.product.price) {
      const normalPrice = this.addToCartForm.get('quantity')?.value * this.product.price.value!;
      this.subtotal.next(normalPrice);
      this.isAddToCartDisabled = !(this.subtotal.getValue() > 0);
    }
  }

  private timeSelectionBtn() {
    if (this.product.timeSelectionRequired) {
      this.addToCartForm.get('timeSlotPicker')!.reset();
      this.addToCartForm.get('timeSlotPicker')?.disable();
    }
  }

   enableOrDisableBtn() {
    if (this.product.dateSelectionRequired) {
      this.isDateSelectionDisabled = false;
      this.isAddToCartDisabled = true;
      this.addToCartForm.get('datePicker')!.reset();
      this.timeSelectionBtn();
      this.subtotal.next(0);
      // Raj: Make the date placeholder text black bold here
      document.querySelector("input[formControlName='datePicker']")?.closest('mat-form-field')?.classList.add('placeholderBold');
    } else {
      this.setSubTotal('');
      this.isAddToCartDisabled = !(this.subtotal.getValue() > 0);
    }

    this.dataTransfer.enableAddtoCartButton(this.isAddToCartDisabled);
  }

  hasPositiveQuantity(product: Product): boolean {
    for (const variant of product.variantOptions!) {
      if (this.addToCartForm.get(variant.code!)?.value > 0) {
        return true;
      }
    }
    return false;
  }

  dateSelected(date: MatDatepickerInputEvent<Date>) {
    if (date.value) {
      this.isDateSelected=true;
      document.querySelector("input[formControlName='datePicker']")?.closest('mat-form-field')?.classList.add('placeholderBold');
      this.cd.markForCheck();
      // reset the timeSlots to empty once selected
      this.timeSlots = new Set<string>();
      if (this.product.timeSelectionRequired) {
        this.addToCartForm.get('timeSlotPicker')?.reset();
        this.addToCartForm.get('timeSlotPicker')?.enable();
        this.isAddToCartDisabled = true;
      } else {
        this.isAddToCartDisabled = false;
      }
      this.addToCartForm.get('datePicker')?.setValue(date.value, { emitEvent: false });
      this.addToCartForm.markAsDirty();
      const selectedDate = this.dateToString(new Date(date.value)).trim();
      this.setSubTotal(selectedDate);
      this.cd.detectChanges();
    }
    else{
      this.isDateSelected=false;
      document.querySelector("input[formControlName='datePicker']")?.closest('mat-form-field')?.classList.remove('placeholderBold');
    }
  }

  timeSlotSelected(event: MatSelectChange) {
    if (event.value) {
      this.isAddToCartDisabled = false;
    }
  }

  setSubTotal(selectedDate: string) {
    let total = 0;
    if (this.currentBaseSite === 'b2bportal') {
    total = this.getSubtotal(selectedDate, total);
    }
    if (this.currentBaseSite != 'b2bportal') {
      //case 1 variant product
      if (this.activeSingleSelectButton != 0) {
        this.product?.variantOptions
          ?.filter((options) => options?.code === this.activeSingleSelectButton)
          .map((currentVariantProd) => {
            if (this.addToCartForm.get(currentVariantProd.code!)?.value > 0) {
              let currentProdQty = this.addToCartForm.get(currentVariantProd.code!)?.value
              if (selectedDate) {
                const calendarDates = this.availableTimeSlotAndPrice.get(currentVariantProd.code);
                const pricing = calendarDates
                  ?.filter((cal: { date: string }) => cal.date === selectedDate)
                  .flatMap((obj: { pricing: [] }) => obj.pricing);
                this.setTimeSlotsForEvents(calendarDates, selectedDate);
                total = total + pricing[0]?.amount * currentProdQty;
              } else {
                const qty = this.addToCartForm.get(currentVariantProd.code!)?.value;
                if (currentVariantProd.startingPrice != undefined && currentVariantProd.startingPrice.value) {
                  total = total + currentVariantProd.startingPrice.value! * qty;
                }
              }
            }
          });
      }

      else {
        total = this.getSubtotal(selectedDate, total);
      }
    }
    if (this.product.udxProductType === 'EXPRESS' && selectedDate) {
      const calendarDates = this.availableTimeSlotAndPrice.get(this.product.code);
      const pricing = calendarDates
        .filter((cal: { date: string }) => cal.date === selectedDate)
        .flatMap((obj: { pricing: [] }) => obj.pricing);
      total = total + pricing[0]?.amount * this.addToCartForm.get('quantity')?.value;
    }
    if (this.product.bundlePrice != undefined && selectedDate && this.addToCartForm.get('quantity')?.value > 0) {
      const calendarDates = this.availableTimeSlotAndPrice.get(this.product.code);
      const pricing = calendarDates
        .filter((cal: { date: string }) => cal.date === selectedDate)
        .flatMap((obj: { pricing: [] }) => obj.pricing);
      this.setTimeSlotsForEvents(calendarDates, selectedDate);

      total = total + pricing[0]?.amount * this.addToCartForm.get('quantity')?.value;
      this.subtotal.next(total);
    }
    if (this.timeSlots.size === 1) {
      const [first] = this.timeSlots;
      this.addToCartForm.get('timeSlotPicker')?.setValue(first);
      this.isAddToCartDisabled = false;
    }
    this.subtotal.next(total);
  }

  private getSubtotal(selectedDate: string, total: number) {
    this.product.variantOptions?.forEach((options) => {
      if (this.addToCartForm.get(options.code!)?.value > 0) {
        if (selectedDate) {
          const calendarDates = this.availableTimeSlotAndPrice.get(options.code);
          const pricing = calendarDates
            ?.filter((cal: { date: string; }) => cal.date === selectedDate)
            .flatMap((obj: { pricing: []; }) => obj.pricing);
          this.setTimeSlotsForEvents(calendarDates, selectedDate);
          total = total + pricing[0]?.amount * this.addToCartForm.get(options.code!)?.value;
          // total = total + pricing[0]?.amount;
        } else {
          const qty = this.addToCartForm.get(options.code!)?.value;
          if (options.startingPrice != undefined && options.startingPrice.value) {
            total = total + options.startingPrice.value! * qty;
          }
        }
      }
    });
    return total;
  }

  private setTimeSlotsForEvents(calendarDates: any, selectedDate: string) {
    // if (this.product.timeSelectionRequired) {
      const events: Udxb2bInventoryEvents[] = calendarDates
        .filter((cal: { date: string }) => cal.date === selectedDate)
        .flatMap((obj: { inventoryEvents: Udxb2bInventoryEvents[] }) => obj.inventoryEvents);

      events.forEach((time: Udxb2bInventoryEvents) => {
        this.extractTimefromStartDate(time);
      });
    // }
  }

  private extractTimefromStartDate(time: Udxb2bInventoryEvents) {
    var timeAndDate = time.startDate!.split(/(\s+)/);
    if (timeAndDate[2] && timeAndDate[4]) {
      this.timeSlots.add(timeAndDate[2] + ' ' + timeAndDate[4]);
    }
  }

  private addToCartSubject = new Subject<boolean>();
  override addToCart(shouldDisableDate = true) {
    // console.log('Invoked addToCart');
    this.addToCartSubject.next(shouldDisableDate);
  }

  performAddToCart(shouldDisableDate=true) {
    // console.log('Invoked performAddToCart');
    this.apiLoading$.next(true);
    const quantity = this.addToCartForm.get('quantity')?.value;

    // Testing:
    // this.activeCartService.addUdxEntry(this.productCode, quantity, new Date());

    if (!this.productCode || quantity <= 0) {
      this.apiLoading$.next(false);
      return;
    }
    var selectedDate: any = null;
    if (this.product.dateSelectionRequired) {
      // selectedDate = this.dateToString(new Date(this.addToCartForm.get('datePicker')?.value)).trim();
      selectedDate = this.getFormattedDate(new Date(this.addToCartForm.get('datePicker')?.value)).trim();
    }
    var timeslot: any = null;
    if (this.product.timeSelectionRequired) {
      timeslot = this.addToCartForm.get('timeSlotPicker')?.value;
      // console.log ("Timeslot value = " + timeslot);
      selectedDate = selectedDate + ' ' + timeslot;
    }

    else{
      if (this.timeSlots.size === 1 && this.currentBaseSite != 'b2bportal') {
        this.timeSlots.forEach(time =>{
          // console.log(time)
          selectedDate = selectedDate + ' ' + time;

        })
      }
    }
    if (selectedDate) {
      selectedDate = selectedDate.trim();
    }
    var orderEntriesArray: any[] = [];
    if (this.product.variantOptions) {
      if (this.product.productVariantSelector == 'SINGLE_SELECT') {
        // Products with which you get productVariantSelector as SINGLE_SELECT
        let variantCode = this.currentSingleSelectedOption.code;
        let variantGroupingCode = this.product.code;
        let variantQuantity = this.addToCartForm.get('quantity')?.value;
        if (variantQuantity) {
          var variantEntryObject: any = {};
          variantEntryObject.quantity = variantQuantity;
          variantEntryObject.product = {
            code: variantCode,
          };
          if (selectedDate) {
            variantGroupingCode = this.setVariantGroupingCode(variantGroupingCode!);
            variantEntryObject.ticketEntryInfo = {
              visitationDate: selectedDate,
              timeSlots: Array.from(this.timeSlots),
            };
          }
          variantEntryObject.variantGroupingCode = variantGroupingCode;
          // USJCOMM-6370:
          variantEntryObject.variantNameForDisplay = this.product.name;
          this.product.variantOptions?.forEach((option) => {
            if(option.code ===this.selectedVariantName)
              {
                if (option.startingPrice) {
                  variantEntryObject.variantPrice = this.unitPrice;
                  variantEntryObject.variantNameForDisplay = option.name;
              }
              }
          });
          orderEntriesArray.push(variantEntryObject);
        }
      } else {
        // Products with variants like Studio passes, Annual pass etc where there are age selectors configured as variants.
        this.product.variantOptions?.forEach((option) => {
          let variantCode = option.code;
          let variantGroupingCode = this.product.code;
          let variantQuantity = this.getVariantQuantity(option);
          if (variantQuantity) {
            var variantEntryObject: any = {};
            variantEntryObject.quantity = variantQuantity;
            variantEntryObject.product = {
              code: variantCode,
            };
            if (selectedDate) {
              variantGroupingCode = this.setVariantGroupingCode(variantGroupingCode!);
              variantEntryObject.ticketEntryInfo = {
                visitationDate: selectedDate,
                timeSlots: Array.from(this.timeSlots),
              };
            }
            variantEntryObject.variantGroupingCode = variantGroupingCode;
            // USJCOMM-6370:
            variantEntryObject.variantNameForDisplay = option.name;
            variantEntryObject.variantPrice = option.startingPrice?.value;
            orderEntriesArray.push(variantEntryObject);
          }
        });
      }
    } else {
      // Products without age variants but direct quantity like Coupon / vouchers
      let variantCode = this.product.code;
      let variantQuantity = this.addToCartForm.get('quantity')?.value;
      if (variantQuantity) {
        var variantEntryObject: any = {};
        variantEntryObject.quantity = variantQuantity;
        variantEntryObject.product = {
          code: variantCode,
        };
        if (selectedDate) {
          variantEntryObject.ticketEntryInfo = {
            visitationDate: selectedDate,
            timeSlots: Array.from(this.timeSlots),
          };
        }
        variantEntryObject.variantPrice = this.product.startingPrice?.value;
        if (this.product.categories) {
          const isVoucher = this.product.categories.some(category => category.code === 'voucher');
          if (isVoucher) {
              variantEntryObject.variantPrice = this.product.price?.value;
          }
      }
        variantEntryObject.variantNameForDisplay = variantCode;
        orderEntriesArray.push(variantEntryObject);
      }
    }
    this.activeCartService
      .getEntries()
      .pipe(take(1))
      .subscribe((cartEntries) => {
        this.apiLoading$.next(false);
        this.activeCartService.addUdxEntry(this.productCode, quantity, orderEntriesArray);
        orderEntriesArray.map( resetEntry => {
          this.addToCartForm.get(resetEntry.product.code)?.setValue(0);
        })
        if(shouldDisableDate)
        {
        this.addToCartForm.get('datePicker')?.setValue(null, { emitEvent: true });
        this.addToCartForm.get('timeSlotPicker')?.setValue(null, { emitEvent: true });

        this.addToCartForm.markAsDirty();
        this.isDateSelected=false;
        document.querySelector("input[formControlName='datePicker']")?.closest('mat-form-field')?.classList.remove('placeholderBold');
        this.isAddToCartDisabled=true;
        }
        this.cartAddEntrySuccessResult$ = this.eventService.get(UdxCartAddEntrySuccessEvent)
           this.subscription.add(
        this.cartAddEntrySuccessResult$.subscribe((event) => {
          if (event && Array.isArray(event.cartModifications)) {
            const successfulEntries = event.cartModifications.filter(
              (entry: { statusCode: string; quantityAdded: number; }) => 
                entry.statusCode === 'success' && entry.quantityAdded > 0
            );
            this.filteredEntries = successfulEntries.map((entry: { entry: any; }) => entry.entry);
            // console.log('Filtered CartAddEntrySuccessEvent: ', this.filteredEntries);
          } else {
            // console.log('CartAddEntrySuccessEvent: No cart modifications found or not an array.');
          }
          let containsVariants = this.product.variantOptions ? true : false;
          this.eventService.dispatch(this.udxCreateCartUiEventAddToCart(this.productCode, quantity, cartEntries.length, containsVariants, orderEntriesArray,this.filteredEntries));
        })
      );

        // A CartUiEventAddToCart is dispatched.  This event is intended for the UI
        // responsible to provide feedback about what was added to the cart, like
        // the added to cart dialog.
        //
        // Because we call activeCartService.getEntries() before, we can be sure the
        // cart library is loaded already and that the event listener exists.
      });
  }
  getVariantQuantity(option: VariantOption) {
    return this.addToCartForm.get(option.code!)?.value;
  }
  protected udxCreateCartUiEventAddToCart(
    productCode: string,
    quantity: number,
    numberOfEntriesBeforeAdd: number,
    inContainsVariants?: boolean,
    inOrderEntriesArray?: any[],
    filteredEntries?: any[],
    storeName?: string,
  ) {
    const newEvent = new UDXCartUiEventAddToCart();
    newEvent.productCode = productCode;
    newEvent.quantity = quantity;
    newEvent.numberOfEntriesBeforeAdd = numberOfEntriesBeforeAdd;
    newEvent.pickupStoreName = storeName;
    newEvent.containsVariants = inContainsVariants;
    newEvent.orderEntriesArray = inOrderEntriesArray;
    newEvent.filteredEntries= filteredEntries
    return newEvent;
  }

  setVariantGroupingCode(variantGroupingCode: string): string {
    const date = new Date(this.addToCartForm.get('datePicker')?.value);
    const timeslot: string = this.addToCartForm.get('timeSlotPicker')?.value;
    if (date) {
      const dateSelected = this.dateToString(date).split('-');
      variantGroupingCode = variantGroupingCode + this.COLONSEPERATOR + dateSelected.join('');
    }
    if (timeslot) {
      const timeSelected: string = timeslot.split(this.COLONSEPERATOR, 2).join('');
      variantGroupingCode = variantGroupingCode + this.COLONSEPERATOR + timeSelected;
    }
    return variantGroupingCode;
  }

  getFormattedDate(date: Date) {
    // Raj: TODO: To move this method to utils
    let year = date.getFullYear();
    let month = (1 + date.getMonth()).toString().padStart(2, '0');
    let day = date.getDate().toString().padStart(2, '0');

    return month + '/' + day + '/' + year;
  }

  toNumber(numberString: string): number {
    return parseInt(numberString);
  }

  singleSelectButtonClickHandler(option: any) {
    this.subtotal.next(0)
    const hideOnCart = (option as Product).hideOnCart;
    if (hideOnCart != undefined && hideOnCart) {
      this.globalMessageService.add({ key: 'pdp.hideOnCart_warning' }, GlobalMessageType.MSG_TYPE_WARNING);
      return;
    }
    this.currentSingleSelectedOption = option;
    this.selectedVariantName = option.code;
    this.activeSingleSelectButton = option.code;
    this.cd.detectChanges();
    this.addToCartForm.get('quantity')!.reset();
    if (this.stepperBtnDisabled(option.stock)) {
      this.addToCartForm.get('quantity')?.disable();
    } else {
      this.addToCartForm.get('quantity')?.enable();
      this.onQuantityChanged(this.addToCartForm.get('quantity')?.value, option.hideOnCart);
    }
  }

  // selectAttractions() {
  //   const dialogRef = this.dialog.open(AttractionSelectionDailogComponent, { height: '80%', width: '40%' });

  //   dialogRef.afterClosed().subscribe((result) => {});
  // }

  getCurrentStoreBaseSite() {
    this._baseSiteService.getActive().pipe(take(1)).subscribe(
      (currentSite) => {
        this.currentBaseSite = currentSite;
        storeFrontConfig
        .filter((config) => config.baseSiteId === currentSite)
        .map((config) => {
          this.fieldEnabled = config.active;
        });
    USJB2BPortalConfig.filter((config) => config.baseSiteId === currentSite).map((config) => {
        this.usjfeature = config.active;
      });        
        switch (this.currentBaseSite) {
          case 'b2bportal':
            this.dateLabel = 'DD/MM/YYYY';
            break;
            default:
              this.dateLabel = 'MM/DD/YYYY';
        }
      }
    );
    this._baseSiteService.get(this.currentBaseSite).pipe(take(1)).subscribe(
      (res) => {
        this.defaultCurrencySymbolForBaseStore = res?.baseStore?.defaultCurrency?.symbol;
        this.defaultCurrencyISO = res?.baseStore?.defaultCurrency?.isocode;
      }
    );
  }
}
